import { createSlice } from '@reduxjs/toolkit';
import _get from 'lodash/get';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getRichTextLongDescription } from '../../services/richTextLongDescriptionService';
import { clearAllProjectData } from '../actions/clearAllProjectData';
import setRichText from '../services/setRichText';
import { getLongDescriptionByDocumentAndAdjustment } from './longDescription';

export const initialState = {
  loading: false,
  isSavingDescription: false,
  isSavingDescriptionFulfilled: false,
  libraryItemLinks: {},
  basisOfPresentation: {
    data: '',
    childrenData: [],
  },
  selectedAdjustment: '',
  longDescription: {
    header: '',
    subheader: '',
    childrenData: [],
    content: {
      plainText: '',
      richText: '',
    },
    isBasisOfPresentationMode: true,
  },
  adjustmentMetadata: {
    libraryItemId: '',
    relatedTextKey: '',
    adjustmentGroup: '',
    adjustmentKey: '',
    documentCode: '',
    projectId: '',
    contentId: '',
    contentVersionNumber: '',
  },
  fileDownload: {
    loading: false,
    hasError: false,
    message: '',
  },
};

const structuredDocumentDescriptionSlice = createSlice({
  name: 'structuredDocumentDescription',
  initialState,
  reducers: {
    resetStructuredDocumentDescription: () => initialState,
    loadDescriptionBegin: (state, action) => {
      const {
        header,
        subheader,
        adjustmentGroup,
        adjustmentKey,
        documentCode,
        projectId,
        contentId,
        contentVersionNumber,
        isAdjustmentLoading,
      } = action.payload;

      state.loading = true;
      // isAdjustmentLoading value is available only when adjustment is clicked.
      state.isAdjustmentLoading = isAdjustmentLoading;
      state.selectedAdjustment = adjustmentKey;
      state.longDescription.header = header;
      state.longDescription.subheader = subheader;

      state.adjustmentMetadata = {
        libraryItemId: undefined,
        relatedTextKey: '',
        adjustmentGroup,
        adjustmentKey,
        documentCode,
        projectId,
        contentId,
        contentVersionNumber,
      };
      state.longDescription.isBasisOfPresentationMode = !isAdjustmentLoading;
    },
    loadDescriptionComplete: (state, action) => {
      const {
        content,
        childrenData,
        libraryItemId,
        relatedTextKey,
        adjustmentGroup,
        adjustmentKey,
      } = action.payload;

      state.loading = false;
      state.isAdjustmentLoading = false;
      state.longDescription.content = content;
      state.longDescription.childrenData = childrenData;

      state.adjustmentMetadata.relatedTextKey = relatedTextKey;
      state.adjustmentMetadata.libraryItemId = libraryItemId;
      state.adjustmentMetadata.adjustmentGroup = adjustmentGroup;
      state.adjustmentMetadata.adjustmentKey = adjustmentKey;
      state.longDescription.isBasisOfPresentationMode = false;
    },
    descriptionResetToBasisOfPresentation: (state) => {
      state.longDescription.header = 'Basis of Presentation';
      state.longDescription.subheader = '';
      state.longDescription.content = {
        plainText: state.basisOfPresentation,
        richText: '',
      };
      state.longDescription.childrenData = [];
      state.longDescription.isBasisOfPresentationMode = true;
      state.selectedAdjustment = '';
    },
    basisOfPresentationSet: (state, action) => {
      state.basisOfPresentation = action.payload;
      state.selectedAdjustment = '';
    },
    libraryItemLinksSet: (state, action) => {
      state.libraryItemLinks[action.payload.documentCode] =
        action.payload.libraryItemLinks;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setDescriptionSaving: (state, action) => {
      state.isSavingDescription = action.payload;
      state.isSavingDescriptionFulfilled = !action.payload;
    },
    setDescriptionSavingFulfilled: (state, action) => {
      state.isSavingDescriptionFulfilled = action.payload;
    },
    setFileDownloadLoading: (state) => {
      state.fileDownload.loading = true;
      state.fileDownload.hasError = false;
      state.fileDownload.message = '';
    },
    setFileDownloadError: (state, action) => {
      state.fileDownload.loading = false;
      state.fileDownload.hasError = true;
      state.fileDownload.message = action.payload;
    },
    resetFileDownload: (state) => {
      state.fileDownload.loading = false;
      state.fileDownload.hasError = false;
      state.fileDownload.message = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearAllProjectData, () => initialState);
  },
});

export default structuredDocumentDescriptionSlice.reducer;

// Actions
export const {
  resetStructuredDocumentDescription,
  loadDescriptionBegin,
  loadDescriptionComplete,
  basisOfPresentationSet,
  libraryItemLinksSet,
  descriptionResetToBasisOfPresentation,
  setLoading,
  setDescriptionSaving,
  setDescriptionSavingFulfilled,
  setFileDownloadLoading,
  setFileDownloadError,
  resetFileDownload,
} = structuredDocumentDescriptionSlice.actions;

// Selectors
export const getLibraryItemLinks = (state, code) =>
  _get(state, `structuredDocumentDescription.libraryItemLinks[${code}]`, []);

export const getAnalysisDescription = (state) => ({
  loading: _get(state, `structuredDocumentDescription.loading`, false),
  isSavingDescription: _get(
    state,
    `structuredDocumentDescription.isSavingDescription`,
    false
  ),
  isSavingDescriptionFulfilled: _get(
    state,
    `structuredDocumentDescription.isSavingDescriptionFulfilled`,
    false
  ),
  selectedAdjustment: _get(
    state,
    `structuredDocumentDescription.selectedAdjustment`,
    ''
  ),
  header: _get(
    state,
    `structuredDocumentDescription.longDescription.header`,
    ''
  ),
  subheader: _get(
    state,
    `structuredDocumentDescription.longDescription.subheader`,
    ''
  ),
  content: _get(
    state,
    `structuredDocumentDescription.longDescription.content`,
    {}
  ),
  childrenData: _get(
    state,
    `structuredDocumentDescription.longDescription.childrenData`,
    []
  ),
  relatedTextKey: _get(
    state,
    `structuredDocumentDescription.adjustmentMetadata.relatedTextKey`,
    ''
  ),
  libraryItemId: _get(
    state,
    `structuredDocumentDescription.adjustmentMetadata.libraryItemId`,
    undefined
  ),
  isBasisOfPresentationMode: _get(
    state,
    `structuredDocumentDescription.longDescription.isBasisOfPresentationMode`
  ),
  basisOfPresentation: _get(
    state,
    `structuredDocumentDescription.basisOfPresentation`,
    false
  ),
  libraryItemLinks: _get(
    state,
    `structuredDocumentDescription.libraryItemLinks`,
    ''
  ),
});

export const getAdjustmentMetadata = (state) =>
  state.structuredDocumentDescription.adjustmentMetadata;

export const getFileDownload = (state) =>
  state.structuredDocumentDescription.fileDownload;

export const getIsAdjustmentLoading = (state) =>
  state.structuredDocumentDescription.isAdjustmentLoading;

export const setDescriptionLoading = (isLoading) => async (dispatch) => {
  dispatch(setLoading({ loading: isLoading }));
};

const saveBasisOfPresentation = async (payload, dispatch) => {
  try {
    const { childItemIds, richTextData, libraryItemId, name } = payload;
    const basisOfPresentation = {
      childrenData: childItemIds,
      data: richTextData,
      libraryItemId,
      relatedTextKey: name,
    };
    dispatch(basisOfPresentationSet(basisOfPresentation));
    await setRichText(payload);
  } catch (error) {
    /** error handling? */
  }
};

// Thunks
export const loadBasisOfPresenation =
  (projectId, documentCode, contentThreadId, versionNumber, isClientView) =>
  async (dispatch, getState) => {
    dispatch(setLoading(true));
    const libraryItemLinks = getLibraryItemLinks(getState(), documentCode);
    const state = getState();
    const isThirdPartyMember =
      state.loggedInUser.actualUserRole === USER_ROLES.THIRD_PARTY;

    const isPwcUser = state.loggedInUser.userRole === USER_ROLES.PWC_USER;

    const dynamicBasisOfPresentation = await getRichTextLongDescription(
      projectId,
      documentCode,
      contentThreadId,
      versionNumber,
      `${documentCode}_BASIS_OF_PRESENTATION_${contentThreadId}`,
      libraryItemLinks,
      isClientView || isThirdPartyMember,
      isPwcUser
    );

    dispatch(basisOfPresentationSet(dynamicBasisOfPresentation));
    dispatch(setLoading(false));
  };

// Thunks

export const setDescriptorForAdjustmentThunk =
  (
    adjustmentGroup,
    adjustmentKey,
    documentCode,
    projectId,
    contentId,
    contentVersionNumber,
    isClientView = false
  ) =>
  async (dispatch, getState) => {
    await dispatch(
      loadDescriptionBegin({
        header: adjustmentGroup,
        subheader: adjustmentKey,
        adjustmentGroup,
        adjustmentKey,
        documentCode,
        projectId,
        contentId,
        contentVersionNumber,
        isAdjustmentLoading: true,
      })
    );

    const state = getState();

    const defaultLongDescription = getLongDescriptionByDocumentAndAdjustment(
      documentCode,
      adjustmentKey
    )(state);

    const isThirdPartyMember =
      state.loggedInUser.actualUserRole === USER_ROLES.THIRD_PARTY;

    const libraryItemLinks = getLibraryItemLinks(getState(), documentCode);
    const dynamicLongDescription = await getRichTextLongDescription(
      projectId,
      documentCode,
      contentId,
      contentVersionNumber,
      defaultLongDescription ? defaultLongDescription.adjustmentRowId : '',
      libraryItemLinks,
      isClientView || isThirdPartyMember
    );

    const descriptionPayload = {
      content: {
        plainText: _get(defaultLongDescription, 'data', ''),
      },
      childrenData: _get(defaultLongDescription, 'childrenData', []),
      isBasisOfPresentationMode: false,
    };

    if (dynamicLongDescription) {
      descriptionPayload.relatedTextKey = dynamicLongDescription.relatedTextKey;
      descriptionPayload.libraryItemId = dynamicLongDescription.libraryItemId;
      descriptionPayload.content = { richText: dynamicLongDescription.data };
      descriptionPayload.childrenData = dynamicLongDescription.childrenData;
      descriptionPayload.adjustmentGroup = adjustmentGroup;
      descriptionPayload.adjustmentKey = adjustmentKey;
    }
    dispatch(loadDescriptionComplete(descriptionPayload));
  };

export const setStructuredAnalysesAdjustmentsThunk =
  (
    adjustmentGroup,
    adjustmentKey,
    documentCode,
    projectId,
    contentId,
    contentVersionNumber,
    isClientView = false
  ) =>
  async (dispatch, getState) => {
    await dispatch(
      loadDescriptionBegin({
        header: adjustmentGroup,
        subheader: adjustmentKey,
        adjustmentGroup,
        adjustmentKey,
        documentCode,
        projectId,
        contentId,
        contentVersionNumber,
      })
    );

    const state = getState();

    const defaultLongDescription = getLongDescriptionByDocumentAndAdjustment(
      documentCode,
      adjustmentKey
    )(getState());

    const isThirdPartyMember =
      state.loggedInUser.actualUserRole === USER_ROLES.THIRD_PARTY;

    const libraryItemLinks = getLibraryItemLinks(getState(), documentCode);

    const dynamicLongDescription = await getRichTextLongDescription(
      projectId,
      documentCode,
      contentId,
      contentVersionNumber,
      defaultLongDescription ? defaultLongDescription.adjustmentRowId : '',
      libraryItemLinks,
      isClientView || isThirdPartyMember
    );

    const descriptionPayload = {
      content: {
        plainText: _get(defaultLongDescription, 'data', ''),
      },
      childrenData: _get(defaultLongDescription, 'childrenData', []),
      isBasisOfPresentationMode: false,
    };

    if (dynamicLongDescription) {
      descriptionPayload.relatedTextKey = dynamicLongDescription.relatedTextKey;
      descriptionPayload.libraryItemId = dynamicLongDescription.libraryItemId;
      descriptionPayload.content = { richText: dynamicLongDescription.data };
      descriptionPayload.childrenData = dynamicLongDescription.childrenData;
    }

    // eslint-disable-next-line no-unneeded-ternary
    return dynamicLongDescription ? dynamicLongDescription : descriptionPayload;
  };

const saveAdjustmentOfDescription = async (payload, dispatch, state) => {
  const { longDescription } = state.structuredDocumentDescription;

  const currentValue = longDescription.content.richText;
  const incomingValue = payload.richTextData;
  if (currentValue !== incomingValue) {
    try {
      dispatch(setDescriptionSaving(true));
      await setRichText(payload);
      dispatch(setDescriptionSaving(false));
    } catch (error) {
      dispatch(setDescriptionSaving(false));
    }
  }
};

export const saveDocumentDescriptionThunk =
  (richTextData, isBasisOfPresentation = false, projectId) =>
  async (dispatch, getState) => {
    const { relatedTextKey, libraryItemId, basisOfPresentation } =
      getAnalysisDescription(getState());
    // Temporary fix for bug 143264
    const richText = richTextData?.data || richTextData;

    // find embedded child item ids
    const root = new DOMParser().parseFromString(richText, 'text/html');
    const childItems = root.querySelectorAll('.placeholder.ck-widget');
    const childItemIds = [];
    childItems.forEach((x) => {
      const value = parseInt(x.getAttribute('id'), 10);
      if (!Number.isNaN(value)) {
        childItemIds.push(value);
      }
    });

    let payload;

    if (isBasisOfPresentation) {
      const { libraryItemId: id, relatedTextKey: name } =
        basisOfPresentation || {};
      payload = {
        projectId,
        libraryItemId: id,
        name,
        richTextData: richText,
        childItemIds,
      };

      const currentValue =
        getState().structuredDocumentDescription.basisOfPresentation.data;
      const incomingValue = richText;
      if (currentValue !== incomingValue) {
        await saveBasisOfPresentation(payload, dispatch);
      }
    } else if (libraryItemId) {
      payload = {
        projectId,
        libraryItemId,
        name: relatedTextKey,
        richTextData: richText,
        childItemIds,
      };

      const currentValue =
        getState().structuredDocumentDescription.longDescription.content
          .richText;
      const incomingValue = payload.richText;
      if (currentValue !== incomingValue) {
        saveAdjustmentOfDescription(payload, dispatch, getState());
      }
    }
  };
